const FR_DIACRITICS_RE = /[ÀÂÄÆÇÉÈÊËÎÏÔŒÙÛÜŸàâäæçéèêëîïôœùûüÿ]/u;

export const hasFrenchDiacritics = (text) => FR_DIACRITICS_RE.test(text || "");

export const isAllCapsWord = (w) => {
  if (!w || typeof w !== "string") return false;
  const letters = w.replace(/[^A-Za-zÀ-ÿ]/g, "");
  return letters.length >= 3 && letters === letters.toUpperCase();
};

const FR_FUNCTION_WORDS = new Set([
  "de",
  "du",
  "des",
  "la",
  "le",
  "les",
  "et",
  "au",
  "aux",
  "à",
  "d'",
  "l'",
  "ou",
]);

const DOMAIN_FR = new Set([
  "féminin",
  "masculin",
  "rugby",
  "hockey",
  "soccer",
  "football",
]);

const ACRONYMS = new Set([
  "USPORTS",
  "NCAA",
  "CIS",
  "FIFA",
  "FIBA",
  "UCI",
  "NHL",
  "NFL",
  "MLS",
  "CFL",
]);

const normalize = (s) => (s || "").toLowerCase();

const isFrenchishWord = (w) => {
  if (!w) return false;
  const n = normalize(w);
  return (
    hasFrenchDiacritics(w) ||
    FR_FUNCTION_WORDS.has(n) ||
    DOMAIN_FR.has(n) ||
    ACRONYMS.has(w)
  );
};

export const isLikelyFrench = (text) => {
  if (!text || typeof text !== "string") return false;
  if (hasFrenchDiacritics(text)) return true;
  const words = text.match(/[\p{L}\p{M}]+/gu) || [];
  if (!words.length) return false;
  const frenchish = words.filter(isFrenchishWord).length;
  return frenchish >= Math.ceil(words.length / 2);
};

const normalizeFrenchDomain = (s) => {
  if (!s) return s;
  return s.replace(/\b(?:aux|des)?\s*femmes\s+rugby\b/gi, "RUGBY FÉMININ");
};

export const translatePreservingFrench = async (
  text,
  targetLang,
  translateFn,
) => {
  if (!text || typeof text !== "string") return text;
  const target = (targetLang || "").toLowerCase();

  if (target !== "fr") {
    return translateFn(text, target);
  }

  const LABEL_VALUE_RE = /^\s*([^:：]{1,200})(\s*[:：]\s*)(.+)$/s;
  const m = text.match(LABEL_VALUE_RE);
  if (m) {
    const [, left, sep, right] = m;
    const rightTrim = right.trim();
    const words = rightTrim.match(/[\p{L}\p{M}]+/gu) || [];
    const frenchishCount = words.filter(isFrenchishWord).length;
    const hasDiac = words.some(hasFrenchDiacritics);
    const majorityFrenchish =
      words.length > 0 && frenchishCount / words.length >= 0.5;

    if (hasDiac || majorityFrenchish) {
      const translatedLeft =
        (await translateFn(left.trim(), "fr")) || left.trim();
      return `${translatedLeft}${sep}${right}`;
    }
  }

  const TOK_RE = /([\p{L}\p{M}]+(?:['’-][\p{L}\p{M}]+)*)|([^\p{L}\p{M}]+)/gu;

  let out = [];
  let buffer = "";
  let inRun = false;

  const flushBuffer = async () => {
    if (!inRun || !buffer) return;
    try {
      const tr = await translateFn(buffer, "fr");
      out.push(tr ?? buffer);
    } catch {
      out.push(buffer);
    }
    buffer = "";
    inRun = false;
  };

  const tokens = Array.from(text.matchAll(TOK_RE));
  for (const t of tokens) {
    const word = t[1];
    const nonWord = t[2];

    if (word !== undefined) {
      if (isFrenchishWord(word)) {
        await flushBuffer();
        out.push(word);
      } else {
        if (!inRun) {
          inRun = true;
          buffer = "";
        }
        buffer += word;
      }
    } else if (nonWord !== undefined) {
      await flushBuffer();
      out.push(nonWord);
    }
  }
  await flushBuffer();

  return normalizeFrenchDomain(out.join(""));
};
